import { FC, useState } from 'react';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import Box from '@mui/material/Box';

import Editor from 'components/Common/TextEditor';
import moment from 'moment';
import { copy } from 'helpers/functions';
import { useTranslation } from 'react-i18next';
import { reqShareDocs } from 'api/requests/main';
import { redirectNewPage } from 'helpers/createLink';
import { Tooltip } from '@mui/material';
import { deleteHtmlFromString } from 'helpers/htmlParser';
import { errorLogger } from 'utils/config/firebase/logs';

interface EditorWithFooterType {
    value: string;
    handleEditorChange: (value: string) => void;
    hasCancel?: boolean;
    onSubmit: () => void;
    onCancel?: () => void;
    isEditable?: boolean;
    title?: string;
    date?: string;
    onCloseParentFullScreen?: () => void;
    onOpenFullScreen?: () => void;
    isFullScreen?: boolean;
    document_id?: string;
    messageId?: string;
    hasGoogleShare?: boolean;
}

const EditorWithFooter: FC<EditorWithFooterType> = ({
    value,
    handleEditorChange,
    hasCancel,
    onSubmit,
    isEditable,
    onCancel,
    title,
    date,
    onCloseParentFullScreen,
    isFullScreen = false,
    onOpenFullScreen,
    document_id,
    messageId,
    hasGoogleShare = false,
}) => {
    const { t } = useTranslation();
    const [disableDoc, setDisableDoc] = useState<boolean>(false);

    const onCopy = () => {
        copy({ value });
    };
    const onShareDocs = async () => {
        if (!document_id || !messageId) {
            return;
        }
        try {
            setDisableDoc(true);
            const response = await reqShareDocs(document_id, messageId);
            const link = response.data.url;
            redirectNewPage(link);
        } catch (error) {
            if (error instanceof Error) {
                errorLogger.critical(error?.message, 'onShareDocs');
            }
            console.log(error);
        } finally {
            setDisableDoc(false);
        }
    };

    const onEnter = () => {
        if (deleteHtmlFromString(value).trim()) {
            onSubmit();
        }
    };

    return (
        <Box className="editor-mode">
            <Editor
                value={value}
                onChange={handleEditorChange}
                title={title}
                isEditable={isEditable}
                onCloseParentFullScreen={onCloseParentFullScreen}
                placeholder={t('web_app.editor.placeholder_2')}
                isFullScreen={isFullScreen}
                onOpenFullScreen={onOpenFullScreen}
                onEnter={onEnter}
            />
            <Box className="editor-footer">
                <Box className="editor-footer-actions">
                    <Box className="editor-footer-actions-information">
                        <Box className="editor-footer-actions-date">
                            {date && `${t('web_app.editor.edited')} ${moment(date).format('lll')}`}
                        </Box>
                        <Box>
                            <Tooltip placement="top" title={t('web_app.tooltips.copy_btn')}>
                                <button onClick={onCopy}>
                                    <ContentCopyOutlinedIcon />
                                </button>
                            </Tooltip>
                            {hasGoogleShare && (
                                <button disabled={disableDoc} onClick={onShareDocs}>
                                    <IosShareOutlinedIcon />
                                </button>
                            )}
                        </Box>
                    </Box>
                    <Box className="footer-btns">
                        {hasCancel && <button onClick={onCancel}>{t('web_app.cancel')}</button>}
                        <button onClick={onEnter}>{t('web_app.done')}</button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default EditorWithFooter;
